import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { ListaAsignada } from '../componentes/ListaAsignada';

interface Sucursal {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly ciudad: string | null;
  readonly domicilio: string | null;
  readonly telefono: string | null;
  readonly activa: boolean;
  /** La lista con la que vende la sucursal, si tiene una propia (E36). */
  readonly listaDePreciosId: string | null;
}

/**
 * Sucursales: el patrón que van a seguir todas las pantallas de administración.
 *
 * Listado denso, alta en un diálogo, y **nada se borra**: una sucursal se desactiva y
 * queda a la vista con su marca, porque tiene comprobantes y movimientos colgando y
 * borrarla dejaría huérfana media historia.
 */
export function Sucursales(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeCrear = usePermiso('EMPRESA.SUCURSAL.CREAR');
  const puedeEditar = usePermiso('EMPRESA.SUCURSAL.EDITAR');

  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [errorDelAlta, setErrorDelAlta] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['sucursales'],
    queryFn: () => pedir<Sucursal[]>('/sucursales'),
  });

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/sucursales', {
        method: 'POST',
        cuerpo: { codigo, nombre, ciudad: ciudad === '' ? undefined : ciudad },
      }),
    onSuccess: async () => {
      setAbierto(false);
      setCodigo('');
      setNombre('');
      setCiudad('');
      setErrorDelAlta(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['sucursales'] });
    },
    onError: (fallo: unknown) => {
      setErrorDelAlta(textoDeError(fallo));
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: (sucursal: Sucursal) =>
      pedir<void>(`/sucursales/${sucursal.id}`, {
        method: 'PATCH',
        cuerpo: { activa: !sucursal.activa },
      }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['sucursales'] });
    },
  });

  const columnas: Columna<Sucursal>[] = [
    {
      clave: 'codigo',
      titulo: t('sucursales.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    { clave: 'nombre', titulo: t('sucursales.nombre'), contenido: (fila) => fila.nombre },
    {
      clave: 'ciudad',
      titulo: t('sucursales.ciudad'),
      contenido: (fila) => fila.ciudad ?? '—',
    },
    {
      clave: 'lista',
      titulo: t('listasDePrecios.asignada'),
      contenido: (fila) => (
        <ListaAsignada
          valor={fila.listaDePreciosId}
          ruta={`/sucursales/${fila.id}`}
          clave="sucursales"
          puedeEditar={puedeEditar}
        />
      ),
    },
    {
      clave: 'estado',
      titulo: t('sucursales.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activa ? 'bien' : 'neutro'}>
          {fila.activa ? t('sucursales.activa') : t('sucursales.inactiva')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: t('sucursales.acciones'),
      contenido: (fila) =>
        puedeEditar ? (
          <Boton
            tono="sutil"
            onClick={() => {
              cambiarEstado.mutate(fila);
            }}
          >
            {fila.activa ? t('acciones.desactivar') : t('acciones.activar')}
          </Boton>
        ) : null,
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('sucursales.titulo')}
        descripcion={t('sucursales.descripcion')}
        acciones={
          puedeCrear ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAbierto(true);
              }}
            >
              {t('sucursales.nueva')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio descripcion={t('sucursales.vacio')} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('sucursales.nueva')}
        descripcion={t('sucursales.ayudaNueva')}
        onCerrar={() => {
          setAbierto(false);
          setErrorDelAlta(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('sucursales.codigo')}
            required
            value={codigo}
            maxLength={32}
            ayuda={t('sucursales.ayudaCodigo')}
            onChange={(evento) => {
              setCodigo(evento.target.value.toUpperCase());
            }}
          />
          <Campo
            etiqueta={t('sucursales.nombre')}
            required
            value={nombre}
            maxLength={160}
            onChange={(evento) => {
              setNombre(evento.target.value);
            }}
          />
          <Campo
            etiqueta={t('sucursales.ciudad')}
            value={ciudad}
            maxLength={120}
            onChange={(evento) => {
              setCiudad(evento.target.value);
            }}
          />
          {errorDelAlta === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {errorDelAlta}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
